/**
 * ReportSheet — contextual report modal for User / Service / Event.
 * Pure local mock: stores a flag in localStorage, shows success.
 */
import { useEffect, useState } from "react";
import { X, ShieldAlert, CheckCircle2, AlertTriangle, Ghost, Flag, ShieldOff, FileText } from "lucide-react";

const REASONS = [
    { id: "scam",    label: "Scam / fraud",   icon: ShieldAlert },
    { id: "fake",    label: "Fake listing",   icon: Ghost },
    { id: "inappropriate", label: "Inappropriate content", icon: ShieldOff },
    { id: "spam",    label: "Spam",           icon: AlertTriangle },
    { id: "other",   label: "Other",          icon: FileText },
];

const ReportSheet = ({ open, onClose, target, onSubmitted }) => {
    // target: { kind: "user"|"service"|"event", id, label }
    const [reason, setReason] = useState(null);
    const [details, setDetails] = useState("");
    const [phase, setPhase] = useState("idle"); // idle | submitting | done

    useEffect(() => {
        if (open) {
            setReason(null);
            setDetails("");
            setPhase("idle");
        }
    }, [open]);

    if (!open || !target) return null;

    const handleSubmit = () => {
        if (!reason) return;
        setPhase("submitting");
        // Mock: store a report record locally
        try {
            const list = JSON.parse(localStorage.getItem("neara.reports") || "[]");
            list.unshift({
                id: "r_" + Date.now(),
                target,
                reason,
                details: details.trim(),
                at: Date.now(),
            });
            localStorage.setItem("neara.reports", JSON.stringify(list));
        } catch { /* noop */ }
        setTimeout(() => {
            setPhase("done");
            onSubmitted?.({ reason, details });
            setTimeout(() => {
                onClose?.();
            }, 1300);
        }, 600);
    };

    const headerLabel =
        target.kind === "user"    ? `Report @${target.label}`     :
        target.kind === "service" ? `Report this service`         :
        target.kind === "event"   ? `Report this event`           :
                                    "Report";

    return (
        <div
            className="absolute inset-0 z-50 flex items-end"
            data-testid="report-sheet"
            role="dialog"
            aria-modal="true"
        >
            <button
                onClick={() => phase === "idle" && onClose?.()}
                aria-label="Close"
                className="absolute inset-0 bg-black/55 animate-fade-in"
                data-testid="report-sheet-scrim"
            />
            <div className="relative z-10 w-full bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.4)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]">
                <div className="flex justify-center pt-2.5 pb-1">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>
                <div className="px-5 pt-1.5 pb-2.5 flex items-center justify-between border-b border-border/60">
                    <div className="min-w-0">
                        <h2 className="font-display text-[16px] font-semibold leading-tight inline-flex items-center gap-1.5">
                            <Flag size={14} className="text-primary" />
                            {headerLabel}
                        </h2>
                        <p className="text-[11.5px] text-muted-foreground mt-0.5">
                            Reports are reviewed within 24 hours.
                        </p>
                    </div>
                    <button
                        onClick={() => phase === "idle" && onClose?.()}
                        className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted disabled:opacity-50"
                        disabled={phase !== "idle"}
                        aria-label="Close"
                        data-testid="report-sheet-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 pt-4 max-h-[60vh] overflow-y-auto no-scrollbar">
                    {phase === "done" ? (
                        <div className="py-6 flex flex-col items-center text-center" data-testid="report-sheet-done">
                            <span
                                className="h-14 w-14 rounded-full flex items-center justify-center text-primary-foreground"
                                style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
                            >
                                <CheckCircle2 size={26} />
                            </span>
                            <h3 className="mt-3 font-display text-[16px] font-semibold">Report submitted</h3>
                            <p className="mt-1 text-[12.5px] text-muted-foreground max-w-xs">
                                Thanks for keeping Neyra safe. Our team will review it.
                            </p>
                        </div>
                    ) : (
                        <>
                            <p className="text-[12px] uppercase tracking-[0.12em] text-muted-foreground font-medium mb-2 ml-1">
                                Reason
                            </p>
                            <ul className="space-y-1.5">
                                {REASONS.map((r) => {
                                    const Icon = r.icon;
                                    const active = reason === r.id;
                                    return (
                                        <li key={r.id}>
                                            <button
                                                type="button"
                                                data-testid={`report-reason-${r.id}`}
                                                onClick={() => setReason(r.id)}
                                                aria-pressed={active}
                                                className={[
                                                    "w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors",
                                                    active
                                                        ? "border-primary bg-primary/5"
                                                        : "border-border bg-secondary hover:bg-muted",
                                                ].join(" ")}
                                            >
                                                <span className={[
                                                    "h-9 w-9 rounded-xl flex items-center justify-center shrink-0",
                                                    active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                                                ].join(" ")}>
                                                    <Icon size={15} />
                                                </span>
                                                <span className="flex-1 text-[13.5px] font-medium">{r.label}</span>
                                                {active && <CheckCircle2 size={16} className="text-primary" />}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>

                            <label className="block mt-4">
                                <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                    More details (optional)
                                </span>
                                <textarea
                                    data-testid="report-details"
                                    value={details}
                                    onChange={(e) => setDetails(e.target.value)}
                                    rows={3}
                                    maxLength={300}
                                    placeholder="Add any context that helps us review faster…"
                                    className="w-full p-3 rounded-2xl border border-border bg-secondary outline-none text-[13.5px] focus:bg-background focus:border-primary resize-none"
                                />
                            </label>
                        </>
                    )}
                </div>

                {phase !== "done" && (
                    <div className="px-5 pt-4">
                        <button
                            type="button"
                            data-testid="report-submit"
                            onClick={handleSubmit}
                            disabled={!reason || phase === "submitting"}
                            className="w-full h-12 rounded-2xl text-primary-foreground font-medium text-[14.5px] active:scale-[0.98] disabled:opacity-50"
                            style={{
                                background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                                boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                            }}
                        >
                            {phase === "submitting" ? "Submitting…" : "Submit report"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReportSheet;
